/**
 * Shared project-level planning runtime snapshots.
 *
 * Live changes are merged by PlanningRuntimeProvider through the unified
 * project SSE stream. Components consume these cache entries; they do not
 * issue their own Axios requests.
 */
import { api } from './api';
import { mergeAgentLogs } from './planningRuntimeCache';

export interface PlanningStatus {
  is_active: boolean;
  design_edit_active?: boolean | null;
  cancelled_phase: string | null;
  run_message_id?: string | null;
  run_status?: string | null;
  pipeline_run_status?: string | null;
  pm_step_status?: string | null;
}

export interface AgentLog {
  id: string;
  project_id: string;
  message_id?: string | null;
  session_id?: string | null;
  agent_type: string;
  action: string;
  summary: string | null;
  created_at: string;
}

export const planningRuntimeApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPlanningStatus: builder.query<PlanningStatus, string>({
      query: (projectId) => ({
        url: '/api/v1/chat/planning-status',
        params: { project_id: projectId },
      }),
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, projectId) => [
        { type: 'PlanningStatus' as const, id: projectId },
      ],
    }),
    getAgentLogs: builder.query<AgentLog[], string>({
      query: (projectId) => `/api/v1/projects/${projectId}/agent-logs`,
      keepUnusedDataFor: 60,
      merge: (currentCache, response) => {
        const merged = mergeAgentLogs(currentCache, response);

        currentCache.splice(0, currentCache.length, ...merged);
      },
      providesTags: (_result, _error, projectId) => [
        { type: 'AgentLogs' as const, id: projectId },
      ],
    }),
  }),
});

export const {
  useGetAgentLogsQuery,
  useGetPlanningStatusQuery,
  useLazyGetAgentLogsQuery,
  useLazyGetPlanningStatusQuery,
} = planningRuntimeApi;
